// What Building readiness's three history cards share: the state chip, a tile, a dated
// line naming who sent a message, the feedback each party owes and every exchange. Each
// area's card (structural, roof condition, on-site survey) adds its own tiles and tables.
// See docs/Y2026-Q4/design-roof-survey-history.md.
import { useState, type ReactNode } from "react";

import type {
  ReadinessAcceptance,
  ReadinessFeedback,
  StructuralBrief,
  StructuralEntry,
  StructuralHistory,
} from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "../utils/attributeFormat";
import { Chevron } from "./Chevron";
import styles from "./EmailHistory.module.css";

export type Cite = (text: string) => ReactNode;

const STATE_CLASS: Record<StructuralHistory["state"]["tone"], string> = {
  dispute: styles.stateDispute,
  wait: styles.stateWait,
  none: styles.stateNone,
  works: styles.stateWorks,
  limits: styles.stateLimits,
  accepted: styles.stateAccepted,
};

export const StateLine = ({ state }: { state: StructuralHistory["state"] }) => (
  <div className={styles.stateLine}>
    <span className={`${styles.state} ${STATE_CLASS[state.tone]}`}>
      {state.label}
    </span>
  </div>
);

export const Tile = ({
  label,
  main,
  sub,
  empty,
}: {
  label: string;
  main: ReactNode;
  sub?: ReactNode;
  empty?: string;
}) => (
  <div className={styles.stage}>
    <div className={styles.stageLabel}>{label}</div>
    {main ? (
      <div className={styles.stageDate}>{main}</div>
    ) : (
      <div className={styles.notRecorded}>{empty}</div>
    )}
    {sub && <div className={styles.stageWho}>{sub}</div>}
  </div>
);

export const BriefLine = ({
  brief,
  cite,
}: {
  brief: StructuralBrief;
  cite: Cite;
}) => (
  <>
    {formatDay(brief.date)}, {brief.sender}, {cite(brief.email)}
  </>
);

/** Who accepted a version, and when: the one that completes the step first. */
export const Acceptances = ({
  accepted,
}: {
  accepted: ReadinessAcceptance[];
}) => (
  <>
    {[...accepted]
      .sort((a, b) => Number(b.completes) - Number(a.completes))
      .map((a) => (
        <span key={a.entry_id} className={styles.note}>
          Accepted by {a.party} {formatDay(a.date)}
          {!a.completes && " (shown, not required)"}
        </span>
      ))}
  </>
);

/** The open feedback points as a tile shows them: how many of how many, and who owes
 * them. */
export const FeedbackTile = ({ history }: { history: ReadinessFeedback }) => (
  <Tile
    label="Open feedback"
    main={
      history.points_total
        ? `${history.points_open} open of ${history.points_total}`
        : ""
    }
    empty="No feedback yet"
    sub={
      history.feedback
        .map((g) => `${g.points.length} with ${g.party}`)
        .join(" · ") || undefined
    }
  />
);

export const FeedbackSection = ({
  history,
  cite,
}: {
  history: ReadinessFeedback;
  cite: Cite;
}) => (
  <div className={styles.section}>
    <h4 className={styles.sectionLabel}>Feedback to be addressed</h4>
    {history.feedback.length === 0 ? (
      <p className={styles.empty}>Nothing raised in feedback is still open.</p>
    ) : (
      history.feedback.map((group) => (
        <div key={group.party} className={styles.party}>
          <h5 className={styles.partyLabel}>
            {group.party}{" "}
            <span className={styles.partyCount}>
              {group.points.length} open
            </span>{" "}
            {group.asks.map((ask) => (
              <a
                key={ask.action_id}
                className={styles.partyAsk}
                href={`#${ask.action_id}`}
              >
                {ask.title}
              </a>
            ))}
          </h5>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Raised</th>
                  <th>Requirement</th>
                  <th>Raised by</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {group.points.map((point) => (
                  <tr key={point.point_id}>
                    <td className={styles.date}>{formatDay(point.raised_on)}</td>
                    <td>{point.text}</td>
                    <td>{point.raised_by}</td>
                    <td>{cite(point.raised_in)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))
    )}
  </div>
);

export const ExchangesSection = ({
  entries: all,
  cite,
  empty,
}: {
  entries: StructuralEntry[];
  cite: Cite;
  /** What the table says where nothing is logged. */
  empty: string;
}) => {
  const [showExchanges, setShowExchanges] = useState(false);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const withdrawn = all.filter((e) => e.withdrawn_reason !== null);
  const entries = showWithdrawn
    ? all
    : all.filter((e) => e.withdrawn_reason === null);
  return (
    <div className={styles.section}>
      <h4 className={styles.sectionLabel}>
        <button
          type="button"
          className={styles.sectionToggle}
          aria-expanded={showExchanges}
          onClick={() => setShowExchanges((v) => !v)}
        >
          Every exchange ({entries.length}) <Chevron open={showExchanges} />
        </button>
        {showExchanges && withdrawn.length > 0 && (
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setShowWithdrawn((v) => !v)}
          >
            {showWithdrawn ? "Hide" : "Show"} {withdrawn.length} withdrawn
          </button>
        )}
      </h4>
      {!showExchanges ? null : entries.length === 0 ? (
        <p className={styles.empty}>
          {withdrawn.length
            ? "Every entry Building readiness logged has been withdrawn."
            : empty}
        </p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>What</th>
                <th>From</th>
                <th>To</th>
                <th>Email</th>
                <th>What it shows</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr
                  key={e.entry_id}
                  className={
                    e.withdrawn_reason !== null ? styles.withdrawn : undefined
                  }
                >
                  <td className={styles.date}>{formatDay(e.date)}</td>
                  <td>
                    <span className={`${styles.tag} ${styles[e.tone]}`}>
                      {e.kind_label}
                    </span>
                    {(e.version || e.about) && (
                      <span className={styles.note}>{e.version || e.about}</span>
                    )}
                  </td>
                  <td>{e.sender}</td>
                  <td>{e.recipients.join("; ")}</td>
                  <td>
                    {cite(e.email)}
                    {e.filed_under && (
                      <span className={styles.note}>
                        Filed under another building ({e.filed_under})
                      </span>
                    )}
                  </td>
                  <td className={styles.summary}>
                    {e.summary}
                    {e.points.length > 0 && (
                      <ul className={styles.points}>
                        {e.points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    )}
                    {e.delivers.length > 0 && (
                      <span className={styles.note}>
                        Delivers {e.delivers.map((k) => k.replace(/_/g, " ")).join(", ")}
                      </span>
                    )}
                    {e.withdrawn_reason !== null && (
                      <span className={styles.note}>
                        Withdrawn: {e.withdrawn_reason}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
